import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Plus } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { useAppStore } from '../../store';
import { ExpressStock } from '../../types';
import ErrorModal from './ErrorModal';
import CategorySizeModal from './CategorySizeModal';

interface ExpressStockModalProps {
  isOpen: boolean;
  onClose: () => void;
  stockId?: string | null;
}

const ExpressStockModal: React.FC<ExpressStockModalProps> = ({
  isOpen,
  onClose,
  stockId,
}) => {
  const { expressStock, addExpressStock, updateExpressStock } = useAppStore();
  const [formData, setFormData] = useState<Omit<ExpressStock, 'id' | 'created_at' | 'updated_at'>>({
    product_type: 'Remera',
    color: '',
    size: 'M',
    quantity: 0,
  });
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showCategorySizeModal, setShowCategorySizeModal] = useState(false);
  const [customCategories, setCustomCategories] = useState<string[]>(['Remera', 'Buzo']);
  const [customSizes, setCustomSizes] = useState<string[]>(['S', 'M', 'L', 'XL']);

  useEffect(() => {
    if (stockId) {
      const stock = expressStock.find(s => s.id === stockId);
      if (stock) {
        setFormData({
          product_type: stock.product_type,
          color: stock.color,
          size: stock.size,
          quantity: stock.quantity,
        });
      }
    } else {
      setFormData({
        product_type: 'Remera',
        color: '',
        size: 'M',
        quantity: 0,
      });
    }
  }, [stockId, expressStock]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (stockId) {
        await updateExpressStock(stockId, formData);
      } else {
        await addExpressStock(formData);
      }
      onClose();
    } catch (error) {
      console.error('Error saving stock:', error);
      setError('Error al guardar el stock');
      setShowErrorModal(true);
    }
  };

  const handleCategoryCreated = (category: string) => {
    if (!customCategories.includes(category)) {
      setCustomCategories([...customCategories, category]);
    }
    setFormData(prev => ({ ...prev, product_type: category as any }));
    setShowCategorySizeModal(false);
  };

  const handleSizeCreated = (size: string) => {
    if (!customSizes.includes(size)) {
      setCustomSizes([...customSizes, size]);
    }
    setFormData(prev => ({ ...prev, size: size as any }));
    setShowCategorySizeModal(false);
  };

  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={onClose}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50" />
          <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[500px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white p-6 shadow-lg">
            <Dialog.Title className="text-xl font-semibold mb-4">
              {stockId ? 'Editar Stock' : 'Crear Item'}
            </Dialog.Title>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <Select
                      label="Categoría"
                      value={formData.product_type}
                      onChange={(value) => setFormData(prev => ({ ...prev, product_type: value as any }))}
                      options={customCategories.map(cat => ({ value: cat, label: cat }))}
                      required
                    />
                  </div>
                  <div className="flex items-center">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      icon={<Plus className="h-4 w-4" />}
                      onClick={() => setShowCategorySizeModal(true)}
                      className="h-12 px-3"
                    >
                      Nueva
                    </Button>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <Select
                      label="Talle"
                      value={formData.size}
                      onChange={(value) => setFormData(prev => ({ ...prev, size: value as any }))}
                      options={customSizes.map(size => ({ value: size, label: size }))}
                      required
                    />
                  </div>
                  <div className="flex items-center">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      icon={<Plus className="h-4 w-4" />}
                      onClick={() => setShowCategorySizeModal(true)}
                      className="h-12 px-3"
                    >
                      Nuevo
                    </Button>
                  </div>
                </div>

                <Input
                  label="Color"
                  id="color"
                  name="color"
                  value={formData.color}
                  onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  required
                />

                <Input
                  label="Cantidad"
                  type="number"
                  id="quantity"
                  name="quantity"
                  value={formData.quantity.toString()}
                  onChange={(e) => setFormData(prev => ({ ...prev, quantity: parseInt(e.target.value) }))}
                  min="0"
                  required
                />
              </div>
              
              <div className="mt-6 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                >
                  {stockId ? 'Guardar Cambios' : 'Crear Item'}
                </Button>
              </div>
            </form>
            
            <Dialog.Close asChild>
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
                aria-label="Close"
              >
                <X className="h-6 w-6" />
              </button>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={error || 'Ha ocurrido un error'}
      />

      <CategorySizeModal
        isOpen={showCategorySizeModal}
        onClose={() => setShowCategorySizeModal(false)}
        onCategoryCreated={handleCategoryCreated}
        onSizeCreated={handleSizeCreated}
      />
    </>
  );
};

export default ExpressStockModal;